<template>
  <nav
    class="pointer-events-none fixed inset-x-0 bottom-0 z-[1100] pb-[env(safe-area-inset-bottom)] pt-0 lg:hidden"
    aria-label="Основная мобильная навигация"
  >
    <div
      class="pointer-events-auto grid w-full grid-cols-6 gap-1 rounded-none border-x-0 border-b-0 border-t border-gray-border/90 bg-white px-2 py-2 shadow-[0_-10px_28px_rgb(0_0_0_/_0.06)] md:px-2.5 md:py-2.5"
    >
      <NuxtLink
        v-for="item in primaryItems"
        :key="item.label"
        :to="item.to"
        class="mobile-nav-item"
        :class="isRouteActive(item) ? 'mobile-nav-item--active' : ''"
      >
        <span class="mobile-nav-item__icon" aria-hidden="true">
          <component
            :is="item.icon"
            class="h-6 w-6"
          />
        </span>
        <span>{{ item.label }}</span>
      </NuxtLink>

      <button
        type="button"
        class="mobile-nav-item"
        :class="isSecondaryRoute ? 'mobile-nav-item--active' : ''"
        @click="$emit('open-menu')"
      >
        <span class="mobile-nav-menu-icon" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
        <span>Меню</span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "#imports";
import FinanceIcon from "~/assets/svg/menu/finance.svg?component";
import GoodsIcon from "~/assets/svg/menu/goods.svg?component";
import HomeIcon from "~/assets/svg/menu/home.svg?component";
import MobileChatIcon from "~/components/mobile/MobileChatIcon.vue";
import OrderIcon from "~/assets/svg/menu/order.svg?component";

type BottomNavItem = {
  label: string;
  to: string;
  aliases: string[];
  exact?: boolean;
  icon: any;
};

defineEmits<{
  (e: "open-menu"): void;
}>();

const route = useRoute();

const primaryItems: BottomNavItem[] = [
  { label: "Главная", to: "/", icon: HomeIcon, exact: true, aliases: ["/"] },
  {
    label: "Каталог",
    to: "/search",
    icon: GoodsIcon,
    aliases: ["/search", "/goods", "/favorites", "/cart"],
  },
  { label: "Заказы", to: "/orders", icon: OrderIcon, aliases: ["/orders"] },
  { label: "Финансы", to: "/finance", icon: FinanceIcon, aliases: ["/finance"] },
  {
    label: "Чат",
    to: "/chat",
    icon: MobileChatIcon,
    aliases: ["/chat"],
  },
];

const normalizePath = (value: string) => value.replace(/\/+$/, "") || "/";

const isRouteActive = (item: BottomNavItem) => {
  const currentPath = normalizePath(route.path);
  const aliases = item.aliases.map((alias) => normalizePath(alias));

  if (item.exact) {
    return aliases.includes(currentPath);
  }

  return aliases.some(
    (alias) => currentPath === alias || currentPath.startsWith(`${alias}/`),
  );
};

const isSecondaryRoute = computed(() => {
  const currentPath = normalizePath(route.path);

  return (
    !primaryItems.some((item) => isRouteActive(item)) &&
    !currentPath.startsWith("/login") &&
    !currentPath.startsWith("/register")
  );
});
</script>

<style scoped>
.mobile-nav-item {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 62px;
  border-radius: 18px;
  padding: 9px 3px 8px;
  font-family: var(--font-helvetica);
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  color: var(--color-extra-text-color);
  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.mobile-nav-item__icon {
  display: inline-flex;
  height: 24px;
  width: 24px;
  align-items: center;
  justify-content: center;
  flex: none;
}

.mobile-nav-item > span:last-child {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-wrap: balance;
}

.mobile-nav-item--active {
  background: var(--color-selected-filter);
  color: var(--color-orange);
  box-shadow: inset 0 0 0 1px rgb(var(--color-orange-rgb) / 0.08);
}

.mobile-nav-item:active {
  transform: translateY(1px);
}

.mobile-nav-menu-icon {
  display: inline-flex;
  height: 24px;
  width: 24px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3.5px;
}

.mobile-nav-menu-icon span {
  display: block;
  height: 2px;
  width: 16px;
  border-radius: 999px;
  background: currentColor;
}

@media (min-width: 768px) and (max-width: 1175px) {
  .mobile-nav-item {
    gap: 5px;
    min-height: 68px;
    border-radius: 20px;
    padding: 11px 5px 10px;
    font-size: 11px;
  }

  .mobile-nav-item__icon {
    height: 26px;
    width: 26px;
  }

  .mobile-nav-menu-icon {
    height: 26px;
    width: 26px;
    gap: 4px;
  }

  .mobile-nav-menu-icon span {
    width: 17px;
  }
}

@media (max-width: 359px) {
  .mobile-nav-item {
    gap: 3px;
    min-height: 58px;
    padding: 8px 1px 7px;
    font-size: 9px;
  }

  .mobile-nav-item__icon {
    height: 22px;
    width: 22px;
  }

  .mobile-nav-menu-icon {
    height: 22px;
    width: 22px;
    gap: 3px;
  }

  .mobile-nav-menu-icon span {
    width: 15px;
  }
}
</style>
